import { EventEmitter, Inject, Injectable, Injector, Output } from '@angular/core';
import { environment } from '../../../../environments/environment';
import { isK3000domain } from '../../../core/common';
import { ToastrDialogService } from '../../toastr-dialog.service';
import { ToastrActionEnum } from 'src/app/utils/enums';
import { TranslateService } from '@ngx-translate/core';
import { LocalStorage } from '../../auth/local.storage';

const DISCOVERY_DOCS = ['https://www.googleapis.com/discovery/v1/apis/drive/v3/rest'];

/** localStorage key used to persist the Google Drive access token across page reloads */
const GOOGLE_DRIVE_TOKEN_KEY = 'GOOGLE_DRIVE_TOKEN';

@Injectable({
  providedIn: 'root'
})
export class GapiSession {

  private readonly clientID: string;
  private readonly scopes: string;

  // GIS token client — replaces gapi.auth2.GoogleAuth
  private tokenClient: google.accounts.oauth2.TokenClient;

  // In-memory token cache
  private currentToken: string | null = null;
  private tokenExpiresAt: number = 0;
  private userEmail: string | null = null;

  @Output() popupWindowClosed: EventEmitter<void> = new EventEmitter();

  private get toastrService() {
    return this.injector.get(ToastrDialogService);
  }

  constructor(
    private readonly translate: TranslateService,
    @Inject(Injector) private readonly injector: Injector,
    private readonly localStorage: LocalStorage,
  ) {
    const driveEnv = isK3000domain() ? environment.kurzweil.drive : environment.alinea.drive;
    this.clientID = driveEnv.clientID;
    this.scopes = driveEnv.scope.join(' ');
  }

  handleClientLoad(): Promise<void> {
    return new Promise((resolve) => {
      // Load gapi.client for Drive API file operations (gapi.client.drive.* still works with GIS)
      gapi.load('client', async () => {
        await gapi.client.init({
          // apiKey removed — when an OAuth bearer token is always set via
          // gapi.client.setToken(), the API key is not needed for Drive requests.
          // The OAuth token identifies both the user and the project for quota.
          discoveryDocs: DISCOVERY_DOCS,
        });

        // Initialize the GIS token client — replaces gapi.auth2.getAuthInstance()
        this.tokenClient = google.accounts.oauth2.initTokenClient({
          client_id: this.clientID,
          scope: this.scopes,
          callback: () => {}, // overridden per-request inside requestNewToken()
        });

        // Restore a previously cached token so the Drive connection survives a page reload.
        const cached = this.localStorage.getWithExpiry<string>(GOOGLE_DRIVE_TOKEN_KEY);
        if (cached) {
          this.currentToken = cached;
          // Derive a generous expiry — getWithExpiry already validated it's not expired.
          // Use 5 minutes as a safe lower-bound; the real expiry is tracked by the TTL set during save.
          this.tokenExpiresAt = Date.now() + 5 * 60 * 1000;
          gapi.client.setToken({ access_token: cached });
          // Eagerly fetch the user email so the settings dialog can show it
          // immediately without waiting for the first Drive API call.
          this.fetchAndCacheUserEmail();
        }

        resolve();
      });
    });
  }

  /**
   * Returns a valid access token, requesting a new one if the cached one
   * is missing or within 60 seconds of expiry.
   * Drop-in replacement for the old getUserToken() — same signature.
   */
  async getUserToken(): Promise<string> {
    if (this.currentToken && Date.now() < this.tokenExpiresAt - 60_000) {
      return this.currentToken;
    }
    return this.requestNewToken();
  }

  get currentUsertoken(): string | null {
    return this.currentToken;
  }

  public isConnected(): boolean {
    return !!this.currentToken && Date.now() < this.tokenExpiresAt;
  }

  /** Returns the connected user's email. Fetches from the userinfo endpoint on
   *  first call and caches in memory; subsequent calls return the cached value.
   *  Returns null when no token is present or the fetch fails. */
  public async getUserInfo(): Promise<string | null> {
    if (this.userEmail) {
      return this.userEmail;
    }
    return this.fetchAndCacheUserEmail();
  }

  public async unAttachDrive(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      if (!this.currentToken) {
        return reject(new Error('No token to revoke'));
      }
      google.accounts.oauth2.revoke(this.currentToken, () => {
        this.currentToken = null;
        this.tokenExpiresAt = 0;
        this.userEmail = null;
        gapi.client.setToken(null);
        this.localStorage.remove(GOOGLE_DRIVE_TOKEN_KEY);
        return resolve();
      });
    });
  }

  // ─── Private ──────────────────────────────────────────────────────────────

  private requestNewToken(): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      this.tokenClient.callback = (response: google.accounts.oauth2.TokenResponse) => {
        if (response.error) {
          this.popupWindowClosed.emit();
          return reject(new Error(response.error_description ?? response.error));
        }

        // Validate that all required scopes were actually granted
        if (!response.scope?.includes('drive')) {
          this.showPermissionWarning();
          // Re-prompt with full consent to force scope selection
          this.tokenClient.requestAccessToken({ prompt: 'consent', scope: this.scopes });
          return;
        }

        this.currentToken = response.access_token;
        this.tokenExpiresAt = Date.now() + Number(response.expires_in) * 1000;

        // Make the token available to all gapi.client.drive.* calls
        gapi.client.setToken({ access_token: response.access_token });

        // Persist the token so the connection survives a page reload.
        // TTL is set to the token's actual lifetime (expires_in is in seconds).
        this.localStorage.setWithExpiry(GOOGLE_DRIVE_TOKEN_KEY, response.access_token, Number(response.expires_in) * 1000);

        // Fetch and cache the user email for display in the settings dialog.
        this.fetchAndCacheUserEmail();

        resolve(response.access_token);
      };

      // Empty prompt reuses existing session silently; 'consent' forces the account picker
      this.tokenClient.requestAccessToken({
        prompt: this.currentToken ? '' : 'consent',
      });
    });
  }

  private showPermissionWarning(): void {
    this.toastrService.showToaster(
      ToastrActionEnum.Warning,
      this.translate.instant('SETTINGS.CLOUD.GOOGLE_DRIVE_AUTHORISATION_WARNING.MESSAGE'),
      this.translate.instant('SETTINGS.CLOUD.GOOGLE_DRIVE_AUTHORISATION_WARNING.TITLE'));
  }

  /** Fetches the authenticated user's email from the Google userinfo endpoint
   *  and stores it in memory. Silently returns null on any error. */
  private async fetchAndCacheUserEmail(): Promise<string | null> {
    if (!this.currentToken) {
      return null;
    }
    try {
      const response = await fetch('https://www.googleapis.com/oauth2/v3/userinfo', {
        headers: { Authorization: `Bearer ${this.currentToken}` },
      });
      if (!response.ok) {
        return null;
      }
      const data = await response.json();
      this.userEmail = data.email ?? null;
      return this.userEmail;
    } catch {
      return null;
    }
  }
}
